import { AmpSettingDto } from '@ampsec/platform-client';

/**
 * The per-tenant flag that gates the customer-facing MCP surface (APD-1818).
 *
 * Mirrors `MCP_EXTERNAL_ENABLED_SETTING_KEY` in amp-platform-api
 * (`src/middleware/ExternalMcpAuthMiddleware.ts`). The API is the enforcement
 * point and fails closed — every MCP call for a tenant without this setting is
 * a 403 — so this key exists only so the UI stops offering credentials that
 * cannot be used.
 */
export const MCP_EXTERNAL_ENABLED_SETTING_KEY = 'securityhub.mcp.external.enabled';

/**
 * Whether the customer-facing MCP surface is enabled for the current tenant.
 *
 * Defaults to disabled — while the settings request is in flight, when the map
 * is missing, and when the key is absent. The server-side default is off, and a
 * tab that flashes in and then vanishes is worse than one that arrives a beat
 * late.
 *
 * The truthy check is deliberately the exact one the API middleware applies
 * (`true` or the string `'true'`), not a lenient parse. A value the UI accepted
 * but the API rejected would show the tab and then 403 every call made with the
 * token issued from it.
 *
 * Takes `unknown` because the settings hook's data type also admits an array;
 * anything that is not a keyed map reads as disabled.
 * @param tenantSettings - The tenant settings map, keyed by setting id.
 * @returns Whether the MCP access tab should be offered.
 */
export const isMcpAccessEnabled = (tenantSettings: unknown): boolean => {
    if (!tenantSettings || typeof tenantSettings !== 'object' || Array.isArray(tenantSettings)) {
        return false;
    }

    const setting = (tenantSettings as { [key: string]: AmpSettingDto | undefined })[
        MCP_EXTERNAL_ENABLED_SETTING_KEY
    ];
    const value = setting?.value as unknown;
    return value === true || value === 'true';
};
